// ═══════════════════════════════════════════════════════════════════════════════
// LivePreviewDrawer — render an agent's briefings against a live patient
// ═══════════════════════════════════════════════════════════════════════════════
//
// PURPOSE
//   Opens from the AgentBriefingTab toolbar. SMEs use this to verify what
//   the LLM actually sees when this agent runs for a chosen patient. Backs
//   POST /api/AgentBriefingContext/Preview/{agentId} (Phase 8).
//
// TWO-STAGE FLOW
//   1. PICKER — search + select a patient on the agent's merchant
//   2. RESULT — patient summary header + per-briefing cards + totals footer
//
// PHI POSTURE
//   The patient summary header is PHI-minimized (initials, masked MRN). The
//   rendered text of each element IS clinical PHI; Studio displays it for
//   review and never persists. The recent-picks chip uses memberId only
//   (no name persisted), and is stored in localStorage scoped to the agent.
//
// COPY-PASTE WORKFLOW
//   SMEs frequently document briefing review in clinical-content QA docs.
//   Each rendered element has a copy button; there is also a "Copy combined
//   text" button in the footer that grabs the exact byte-equivalent of what
//   the LLM would see in the system prompt.
// ═══════════════════════════════════════════════════════════════════════════════

import { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Drawer,
  Empty,
  Input,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  CopyOutlined,
  ExclamationCircleOutlined,
  EyeOutlined,
  MinusCircleOutlined,
  ReloadOutlined,
  SearchOutlined,
  StopOutlined,
  ThunderboltOutlined,
  UserOutlined,
} from '@ant-design/icons';
import {
  previewBriefingForAgent,
  searchMembersForPreview,
  type BriefingPreviewBriefing,
  type BriefingPreviewElement,
  type BriefingPreviewResult,
  type MemberSearchHit,
} from '@/api/briefingContext';
import { StatusPill, type PillTone } from '@/components/StatusPill';
import { colors, typography } from '@/theme/tokens';

const { Text, Title } = Typography;

interface LivePreviewDrawerProps {
  open: boolean;
  agentId: number;
  /** Merchant scope for member search. Falls back to the agent merchant on the server. */
  merchantId: number | null;
  onClose: () => void;
}

// Drawer-local lifecycle. Kept as a string so the body switches cleanly.
type Stage = 'pick' | 'rendering' | 'loaded' | 'error';

// Recent-picks live in localStorage so a SME triaging back-to-back agents
// against the same patient doesn't have to retype the search. Scoped to
// the agentId so each agent has its own short-list.
interface RecentPick {
  memberId: number;
  /** Display label — initials + a hint (e.g. "J.M. · age 62"). Never the full name. */
  hint: string;
}

const RECENT_PICKS_KEY = (agentId: number) => `agentstudio.briefingPreview.recent.${agentId}`;
const RECENT_PICKS_MAX = 5;

function readRecentPicks(agentId: number): RecentPick[] {
  try {
    const raw = window.localStorage.getItem(RECENT_PICKS_KEY(agentId));
    if (!raw) return [];
    const parsed = JSON.parse(raw) as RecentPick[];
    return Array.isArray(parsed) ? parsed.slice(0, RECENT_PICKS_MAX) : [];
  } catch {
    return [];
  }
}

function writeRecentPick(agentId: number, pick: RecentPick) {
  try {
    const prior = readRecentPicks(agentId).filter((p) => p.memberId !== pick.memberId);
    const next = [pick, ...prior].slice(0, RECENT_PICKS_MAX);
    window.localStorage.setItem(RECENT_PICKS_KEY(agentId), JSON.stringify(next));
  } catch {
    // localStorage may be unavailable (private mode, quota). Non-fatal.
  }
}

export function LivePreviewDrawer({ open, agentId, merchantId, onClose }: LivePreviewDrawerProps) {
  const [stage, setStage] = useState<Stage>('pick');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [result, setResult] = useState<BriefingPreviewResult | null>(null);

  // Stage 1 state
  const [query, setQuery] = useState('');
  const [searchHits, setSearchHits] = useState<MemberSearchHit[]>([]);
  const [searching, setSearching] = useState(false);
  const [recent, setRecent] = useState<RecentPick[]>([]);

  // Stage 2 state
  const [showCombined, setShowCombined] = useState(false);

  const inputRef = useRef<{ focus: () => void } | null>(null);

  useEffect(() => {
    if (!open) {
      // Reset everything on close so re-opening starts at the picker.
      setStage('pick');
      setErrorMsg(null);
      setResult(null);
      setQuery('');
      setSearchHits([]);
      setShowCombined(false);
      return;
    }
    setRecent(readRecentPicks(agentId));
    const t = setTimeout(() => inputRef.current?.focus(), 220);
    return () => clearTimeout(t);
  }, [open, agentId]);

  // ── Search (debounced) ───────────────────────────────────────────────────
  useEffect(() => {
    if (stage !== 'pick') return;
    const trimmed = query.trim();
    if (trimmed.length < 2) {
      setSearchHits([]);
      return;
    }
    if (!merchantId || merchantId <= 0) return;
    let cancelled = false;
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const hits = await searchMembersForPreview(merchantId, trimmed, 20);
        if (!cancelled) setSearchHits(hits);
      } catch (e: unknown) {
        if (!cancelled) message.error(e instanceof Error ? e.message : 'Member search failed');
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [query, merchantId, stage]);

  async function runPreview(memberId: number, hint: string) {
    setStage('rendering');
    setErrorMsg(null);
    try {
      const data = await previewBriefingForAgent(agentId, memberId);
      setResult(data);
      writeRecentPick(agentId, { memberId, hint });
      setStage('loaded');
    } catch (e: unknown) {
      setErrorMsg(e instanceof Error ? e.message : 'Preview failed');
      setStage('error');
    }
  }

  function handlePickHit(hit: MemberSearchHit) {
    const label = computeMemberHint(hit);
    void runPreview(hit.id, label);
  }

  function handlePickRecent(pick: RecentPick) {
    void runPreview(pick.memberId, pick.hint);
  }

  async function handleCopyCombined() {
    if (!result?.combinedPromptText) return;
    try {
      await navigator.clipboard.writeText(result.combinedPromptText);
      message.success('Combined LLM-facing text copied');
    } catch {
      message.error('Copy failed — your browser blocked clipboard access');
    }
  }

  async function handleCopyElement(el: BriefingPreviewElement) {
    if (!el.text) return;
    try {
      await navigator.clipboard.writeText(el.text);
      message.success(`Copied — ${el.displayName ?? el.elementCode}`);
    } catch {
      message.error('Copy failed — your browser blocked clipboard access');
    }
  }

  // ── Render ───────────────────────────────────────────────────────────────

  const hasMerchant = !!merchantId && merchantId > 0;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      placement="right"
      width={720}
      closable={false}
      title={null}
      styles={{
        body: { padding: 0, display: 'flex', flexDirection: 'column' },
        header: { display: 'none' },
      }}
    >
      {/* ─── Sticky header ───────────────────────────────────────────────── */}
      <div
        style={{
          padding: '16px 20px 12px',
          borderBottom: `1px solid ${colors.slate[200]}`,
          background: '#FFFFFF',
          flexShrink: 0,
          display: 'flex',
          alignItems: 'flex-start',
          gap: 14,
        }}
      >
        <EyeOutlined style={{ fontSize: 18, color: colors.violet[600], marginTop: 2 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Title level={5} style={{ margin: 0 }}>
              Live preview
            </Title>
            {stage === 'loaded' && result && (
              <Text type="secondary" style={{ fontSize: 11.5 }}>
                rendered in {(result.elapsedMs / 1000).toFixed(2)}s
              </Text>
            )}
          </div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {stage === 'pick'
              ? 'Pick a patient. The drawer renders every active briefing linked to this agent against their data — exactly what the LLM would see.'
              : stage === 'rendering'
              ? 'Rendering every active briefing for this patient. Each element gets a 1-second budget — slow ones are skipped, just like at runtime.'
              : stage === 'error'
              ? 'Something stopped the preview from running. See details below.'
              : result
              ? <PatientChip result={result} />
              : null}
          </Text>
        </div>
        <Button size="small" onClick={onClose}>
          Close
        </Button>
      </div>

      {/* ─── Body ────────────────────────────────────────────────────────── */}
      <div style={{ flex: 1, overflowY: 'auto', background: colors.slate[50] }}>
        {stage === 'pick' && (
          <PatientPicker
            hasMerchant={hasMerchant}
            query={query}
            onQueryChange={setQuery}
            searching={searching}
            hits={searchHits}
            recent={recent}
            onPick={handlePickHit}
            onPickRecent={handlePickRecent}
            inputRefSetter={(r) => (inputRef.current = r)}
          />
        )}

        {stage === 'rendering' && (
          <div style={{ padding: '60px 20px', textAlign: 'center' }}>
            <ThunderboltOutlined spin style={{ fontSize: 24, color: colors.violet[600] }} />
            <div style={{ marginTop: 14 }}>
              <Text style={{ fontSize: 13 }}>Rendering every briefing for this patient…</Text>
            </div>
            <Text type="secondary" style={{ fontSize: 11.5 }}>
              Typically 0.5–2s per agent. Long-running elements get cut at 1s, same as production.
            </Text>
          </div>
        )}

        {stage === 'error' && (
          <div style={{ padding: 20 }}>
            <Alert
              type="warning"
              showIcon
              message="Could not render preview"
              description={errorMsg ?? 'Unknown error.'}
            />
            <div style={{ marginTop: 14 }}>
              <Button icon={<ArrowLeftOutlined />} onClick={() => setStage('pick')}>
                Pick a different patient
              </Button>
            </div>
          </div>
        )}

        {stage === 'loaded' && result && (
          <PreviewBody
            result={result}
            showCombined={showCombined}
            onCopyElement={(el) => void handleCopyElement(el)}
          />
        )}
      </div>

      {/* ─── Sticky footer ───────────────────────────────────────────────── */}
      {stage === 'loaded' && result && (
        <div
          style={{
            padding: '10px 20px',
            borderTop: `1px solid ${colors.slate[200]}`,
            background: '#FFFFFF',
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            flexWrap: 'wrap',
          }}
        >
          <TotalsRow result={result} />
          <span style={{ flex: 1 }} />
          <Tooltip title="Show the exact byte-equivalent text the LLM would see in the system prompt.">
            <Button
              size="small"
              onClick={() => setShowCombined((v) => !v)}
              type={showCombined ? 'primary' : 'default'}
            >
              {showCombined ? 'Hide combined text' : 'Show combined text'}
            </Button>
          </Tooltip>
          <Tooltip title="Copy the concatenated rendered text to clipboard.">
            <Button
              size="small"
              icon={<CopyOutlined />}
              onClick={() => void handleCopyCombined()}
              disabled={!result.combinedPromptText}
            >
              Copy combined
            </Button>
          </Tooltip>
          <Tooltip title="Pick a different patient (resets the preview).">
            <Button size="small" icon={<ArrowLeftOutlined />} onClick={() => setStage('pick')}>
              Change patient
            </Button>
          </Tooltip>
          <Tooltip title="Re-run the preview for the same patient. Useful after editing a briefing or an element in another tab.">
            <Button
              size="small"
              icon={<ReloadOutlined />}
              onClick={() => void runPreview(result.memberId, result.memberInitials ?? '')}
            >
              Re-render
            </Button>
          </Tooltip>
        </div>
      )}
    </Drawer>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────────────────────

function PatientChip({ result }: { result: BriefingPreviewResult }) {
  const bits: string[] = [];
  if (result.memberInitials) bits.push(result.memberInitials);
  if (result.memberAge != null) bits.push(`age ${result.memberAge}`);
  if (result.memberSex) bits.push(result.memberSex);
  if (result.memberLanguageCode && result.memberLanguageCode !== 'en') bits.push(result.memberLanguageCode);
  if (result.memberMrnMasked) bits.push(`MRN ${result.memberMrnMasked}`);
  return (
    <span style={{ fontSize: 12, color: colors.slate[700] }}>
      Patient: <strong>{bits.join(' · ')}</strong>
    </span>
  );
}

// ── Stage 1: Patient picker ──────────────────────────────────────────────────

interface PatientPickerProps {
  hasMerchant: boolean;
  query: string;
  onQueryChange: (v: string) => void;
  searching: boolean;
  hits: MemberSearchHit[];
  recent: RecentPick[];
  onPick: (hit: MemberSearchHit) => void;
  onPickRecent: (pick: RecentPick) => void;
  inputRefSetter: (r: { focus: () => void } | null) => void;
}

function PatientPicker({
  hasMerchant,
  query,
  onQueryChange,
  searching,
  hits,
  recent,
  onPick,
  onPickRecent,
  inputRefSetter,
}: PatientPickerProps) {
  if (!hasMerchant) {
    return (
      <div style={{ padding: 20 }}>
        <Alert
          type="info"
          showIcon
          message="Pick a merchant to enable live preview"
          description="Live preview searches members within the agent's merchant scope. The agent appears global, so pick a merchant from the header chip first."
        />
      </div>
    );
  }
  return (
    <div style={{ padding: '12px 20px 20px' }}>
      <Input
        ref={inputRefSetter as never}
        prefix={<SearchOutlined style={{ color: colors.slate[400] }} />}
        placeholder="Search by first name (2+ characters)…"
        value={query}
        onChange={(e) => onQueryChange(e.target.value)}
        allowClear
      />
      <Text type="secondary" style={{ display: 'block', marginTop: 8, fontSize: 11.5 }}>
        Searches members on this agent&apos;s merchant. The preview only renders for one patient at a time.
      </Text>

      {recent.length > 0 && query.trim().length < 2 && (
        <div style={{ marginTop: 18 }}>
          <Text type="secondary" style={{ fontSize: 11.5, textTransform: 'uppercase', letterSpacing: 0.4 }}>
            Recently previewed
          </Text>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
            {recent.map((r) => (
              <Tag
                key={r.memberId}
                onClick={() => onPickRecent(r)}
                style={{
                  cursor: 'pointer',
                  padding: '4px 10px',
                  fontSize: 12,
                  borderRadius: 16,
                  background: '#FFFFFF',
                }}
                icon={<UserOutlined />}
              >
                {r.hint}
              </Tag>
            ))}
          </div>
        </div>
      )}

      {query.trim().length >= 2 && (
        <div style={{ marginTop: 18 }}>
          {searching ? (
            <div style={{ padding: '20px 0', textAlign: 'center' }}>
              <ThunderboltOutlined spin style={{ fontSize: 16, color: colors.violet[600], marginRight: 8 }} />
              <Text type="secondary" style={{ fontSize: 12 }}>
                Searching…
              </Text>
            </div>
          ) : hits.length === 0 ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <Text type="secondary" style={{ fontSize: 12 }}>
                  No members match &quot;{query}&quot; in this merchant.
                </Text>
              }
            />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {hits.map((hit) => (
                <button
                  key={hit.id}
                  type="button"
                  onClick={() => onPick(hit)}
                  style={{
                    textAlign: 'left',
                    border: `1px solid ${colors.slate[200]}`,
                    background: '#FFFFFF',
                    padding: '10px 12px',
                    borderRadius: 8,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                  }}
                >
                  <UserOutlined style={{ color: colors.slate[400], fontSize: 16 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Text strong style={{ fontSize: 13 }}>
                      {(hit.firstName ?? '').trim()} {(hit.lastName ?? '').trim()}
                    </Text>
                    <div>
                      <Text type="secondary" style={{ fontSize: 11.5 }}>
                        {computeMemberHint(hit)}
                      </Text>
                    </div>
                  </div>
                  <Text style={{ fontSize: 11, color: colors.slate[400] }}>id #{hit.id}</Text>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function computeMemberHint(hit: MemberSearchHit): string {
  const fi = hit.firstName?.trim()[0] ?? '';
  const li = hit.lastName?.trim()[0] ?? '';
  const initials = `${fi}.${li}`.toUpperCase().replace(/^\.$|\.$/g, '');
  const bits: string[] = [];
  if (initials) bits.push(initials);
  if (hit.dob) {
    const d = new Date(hit.dob);
    if (!Number.isNaN(d.getTime())) {
      const age = Math.floor((Date.now() - d.getTime()) / (1000 * 60 * 60 * 24 * 365.25));
      if (age >= 0 && age < 130) bits.push(`age ${age}`);
    }
  }
  if (hit.gender) {
    const g = hit.gender.trim().toUpperCase();
    if (g.startsWith('M')) bits.push('M');
    else if (g.startsWith('F')) bits.push('F');
  }
  if (hit.mrn) bits.push(`MRN •••${hit.mrn.slice(-4)}`);
  return bits.join(' · ');
}

// ── Stage 2: Preview body ────────────────────────────────────────────────────

interface PreviewBodyProps {
  result: BriefingPreviewResult;
  showCombined: boolean;
  onCopyElement: (el: BriefingPreviewElement) => void;
}

function PreviewBody({ result, showCombined, onCopyElement }: PreviewBodyProps) {
  if (result.briefings.length === 0) {
    return (
      <div style={{ padding: 24 }}>
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <div style={{ maxWidth: 380, margin: '0 auto' }}>
              <Text type="secondary" style={{ fontSize: 13 }}>
                This agent has no active+published briefings linked. Link a briefing
                from the toolbar first, then come back to preview.
              </Text>
            </div>
          }
        />
      </div>
    );
  }
  return (
    <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 16 }}>
      {result.briefings.map((b) => (
        <BriefingCard key={b.briefingContextId} briefing={b} onCopyElement={onCopyElement} />
      ))}

      {showCombined && (
        <div
          style={{
            border: `1px solid ${colors.violet[200]}`,
            background: colors.violet[50],
            borderRadius: 10,
            padding: 14,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 8 }}>
            <Text strong style={{ fontSize: 13.5, color: colors.violet[700] }}>
              Combined LLM-facing text
            </Text>
            <Text type="secondary" style={{ fontSize: 11.5 }}>
              this is exactly what the model would receive in the system-prompt briefing block
            </Text>
          </div>
          <pre
            style={{
              margin: 0,
              padding: 12,
              borderRadius: 6,
              background: '#FFFFFF',
              border: `1px solid ${colors.slate[200]}`,
              fontFamily: typography.fontMono,
              fontSize: 12,
              lineHeight: 1.55,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              maxHeight: 360,
              overflow: 'auto',
            }}
          >
            {result.combinedPromptText || '(no element rendered)'}
          </pre>
        </div>
      )}
    </div>
  );
}

function BriefingCard({
  briefing,
  onCopyElement,
}: {
  briefing: BriefingPreviewBriefing;
  onCopyElement: (el: BriefingPreviewElement) => void;
}) {
  const renderedInBriefing = briefing.elements.filter((e) => e.status === 'Rendered').length;
  const totalInBriefing = briefing.elements.length;
  return (
    <div
      style={{
        background: '#FFFFFF',
        border: `1px solid ${colors.slate[200]}`,
        borderRadius: 10,
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          gap: 8,
          padding: '10px 14px',
          borderBottom: `1px solid ${colors.slate[100]}`,
          flexWrap: 'wrap',
        }}
      >
        <Text strong style={{ fontSize: 13.5, color: colors.slate[900] }}>
          {briefing.name || briefing.code}
        </Text>
        <Text type="secondary" style={{ fontFamily: typography.fontMono, fontSize: 11.5 }}>
          {briefing.code}
        </Text>
        <Text type="secondary" style={{ fontFamily: typography.fontMono, fontSize: 11 }}>
          v{briefing.version}
        </Text>
        <span style={{ flex: 1 }} />
        <Text type="secondary" style={{ fontSize: 11 }}>
          {renderedInBriefing} of {totalInBriefing} rendered
        </Text>
      </div>

      <div style={{ padding: '6px 0' }}>
        {briefing.elements.map((el, idx) => (
          <ElementRow
            key={`${el.elementCode}-${idx}`}
            el={el}
            onCopy={() => onCopyElement(el)}
          />
        ))}
      </div>
    </div>
  );
}

function ElementRow({ el, onCopy }: { el: BriefingPreviewElement; onCopy: () => void }) {
  const tone = toneForStatus(el.status);
  const icon = iconForStatus(el.status);
  return (
    <div
      style={{
        padding: '10px 14px',
        borderTop: `1px dashed ${colors.slate[100]}`,
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <StatusPill tone={tone} icon={icon} size="sm">
          {statusLabel(el.status)}
        </StatusPill>
        <Text strong style={{ fontSize: 12.5, color: colors.slate[900] }}>
          {el.displayName || el.elementCode}
        </Text>
        <Text type="secondary" style={{ fontFamily: typography.fontMono, fontSize: 11 }}>
          {el.elementCode}
        </Text>
        {el.category && (
          <Tag
            style={{
              marginRight: 0,
              fontSize: 10.5,
              padding: '0 6px',
              background: colors.slate[100],
              borderColor: colors.slate[200],
              color: colors.slate[700],
            }}
          >
            {el.category}
          </Tag>
        )}
        <Tag
          style={{
            marginRight: 0,
            fontSize: 10.5,
            padding: '0 6px',
            background: el.tier === 1 ? colors.ink[50] : colors.amber[100],
            borderColor: el.tier === 1 ? colors.ink[500] : colors.amber[600],
            color: el.tier === 1 ? colors.ink[700] : colors.amber[700],
          }}
        >
          Tier {el.tier === 1 ? '1 · stable' : '2 · per-turn'}
        </Tag>
        <span style={{ flex: 1 }} />
        {el.status === 'Rendered' && (
          <>
            <Text type="secondary" style={{ fontSize: 11 }}>
              {el.elapsedMs} ms
            </Text>
            <Tooltip title="Copy this element's rendered text">
              <Button
                size="small"
                type="text"
                icon={<CopyOutlined />}
                onClick={onCopy}
                aria-label={`Copy ${el.displayName ?? el.elementCode}`}
              />
            </Tooltip>
          </>
        )}
      </div>

      {el.status === 'Rendered' && el.text && (
        <pre
          style={{
            margin: '2px 0 0 0',
            padding: '8px 10px',
            background: colors.slate[50],
            border: `1px solid ${colors.slate[200]}`,
            borderRadius: 6,
            fontFamily: typography.fontMono,
            fontSize: 12,
            lineHeight: 1.5,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            color: colors.slate[800],
          }}
        >
          {el.text}
        </pre>
      )}

      {el.status === 'Empty' && (
        <Text type="secondary" style={{ fontSize: 11.5, fontStyle: 'italic' }}>
          Provider returned no data for this patient — element omitted from the system prompt.
        </Text>
      )}

      {el.status === 'Deduped' && (
        <Text type="secondary" style={{ fontSize: 11.5, fontStyle: 'italic' }}>
          Already rendered by{' '}
          <span style={{ fontFamily: typography.fontMono }}>{el.dedupedFromBriefingCode}</span>
          {' '}— this briefing&apos;s copy was skipped at runtime.
        </Text>
      )}

      {el.status === 'NoProvider' && (
        <Text type="secondary" style={{ fontSize: 11.5, fontStyle: 'italic' }}>
          Element is in the catalog but no C# provider is registered for it. The runtime would
          skip this element entirely.
        </Text>
      )}

      {el.status === 'Fault' && el.faultMessage && (
        <div
          style={{
            padding: '6px 10px',
            background: colors.red[100],
            border: `1px solid ${colors.red[600]}33`,
            borderRadius: 6,
            fontSize: 11.5,
            color: colors.red[700],
          }}
        >
          {el.faultMessage}
        </div>
      )}
    </div>
  );
}

function TotalsRow({ result }: { result: BriefingPreviewResult }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
      <StatusPill tone="green" icon={<CheckCircleOutlined />} size="sm">
        {result.renderedCount} rendered
      </StatusPill>
      {result.emptyCount > 0 && (
        <StatusPill tone="slate" icon={<MinusCircleOutlined />} size="sm">
          {result.emptyCount} empty
        </StatusPill>
      )}
      {result.dedupedCount > 0 && (
        <StatusPill tone="ink" icon={<StopOutlined />} size="sm">
          {result.dedupedCount} deduped
        </StatusPill>
      )}
      {result.noProviderCount > 0 && (
        <StatusPill tone="amber" icon={<ExclamationCircleOutlined />} size="sm">
          {result.noProviderCount} no provider
        </StatusPill>
      )}
      {result.faultCount > 0 && (
        <StatusPill tone="red" icon={<CloseCircleOutlined />} size="sm">
          {result.faultCount} faulted
        </StatusPill>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Status → tone / icon / label
// ─────────────────────────────────────────────────────────────────────────────

function toneForStatus(s: BriefingPreviewElement['status']): PillTone {
  switch (s) {
    case 'Rendered':   return 'green';
    case 'Empty':      return 'slate';
    case 'Deduped':    return 'ink';
    case 'NoProvider': return 'amber';
    case 'Fault':      return 'red';
  }
}

function iconForStatus(s: BriefingPreviewElement['status']) {
  switch (s) {
    case 'Rendered':   return <CheckCircleOutlined />;
    case 'Empty':      return <MinusCircleOutlined />;
    case 'Deduped':    return <StopOutlined />;
    case 'NoProvider': return <ExclamationCircleOutlined />;
    case 'Fault':      return <CloseCircleOutlined />;
  }
}

function statusLabel(s: BriefingPreviewElement['status']): string {
  switch (s) {
    case 'Rendered':   return 'Rendered';
    case 'Empty':      return 'Empty';
    case 'Deduped':    return 'Deduped';
    case 'NoProvider': return 'No provider';
    case 'Fault':      return 'Fault';
  }
}
